import { useContext, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import classNames from 'classnames'

import {
  EMPTY_LIST,
  EMPTY_STRING,
  FLOATING_ELEMENT_PLACEMENT,
} from '../../../constants/common'
import { FILE_SCOPE_EMAIL } from '../../../constants/file'
import { SIZE } from '../../../constants/size'
import { useThemeMode } from '../../../hooks'
import { getQaSelector } from '../../../utils/common'
import {
  getPreviewGoogleTemplateDependencies,
  getPreviewRecipientValue,
  getRecipientRealId,
} from '../../../utils/email'
import { preventDefault, stopPropagation } from '../../../utils/events'
import { openHtmlLinksInNewTab } from '../../../utils/html'
import { getThemeContentHtml } from '../../../utils/themeContent'
import Button, { BUTTON_HEIGHT_40 } from '../../Button'
import { ConfirmationModal } from '../../Confirmation'
import { Divider } from '../../Divider'
import { FileAttachTag, FileList } from '../../File'
import {
  FilesPreviewGallery,
  useFilePreviewGallery,
} from '../../FilesPreviewGallery'
import { FormActions } from '../../Form'
import Modal, { Box, Content, Header } from '../../Modal'
import {
  ButtonSelect,
  ButtonTextSelectControl,
  SelectDropdownListHeader,
  SelectDropdownListSearchInput,
} from '../../Select'
import {
  TablePaginationNavigation,
  TablePaginationPages,
} from '../../TablePagination'
import { TAG_HEIGHT } from '../../Tag'
import { LINKED_FILES_ITEM_ID_ATTRIBUTE } from '../../WysiwygEditor'
import {
  createEmailPreviewTemplateMessageText,
  getEmailGoogleTemplatePreview,
} from '../Email.api'
import { EmailActionDeleteButton } from '../EmailActionDeleteButton'
import { EmailFormContext } from '../EmailForm.context'

import {
  QA_NAME_FROM_FIELD,
  QA_NAME_MESSAGE_FIELD,
  QA_NAME_PREVIEW_MODAL,
  QA_NAME_SUBJECT_FIELD,
  QA_NAME_TO_FIELD,
  RECIPIENTS_DROPDOWN_MAX_HEIGHT,
} from './EmailPreviewModal.constants'
import {
  filterByFullNameAndEmail,
  getGeneratedTemplatePdf,
  getNextFiles,
  getPreviewTextParams,
  getPrevRecipientIndex,
  getSelectedRecipient,
} from './EmailPreviewModal.utils'
import { EmailPreviewTextField } from './EmailPreviewTextField'
import { EmailPreviewUserField } from './EmailPreviewUserField'

import * as styles from './EmailPreviewModal.module.css'

function EmailPreviewModal({
  getPreviewDependencies,
  linkedFiles,
  templateEntityApiParams,
  textTemplate,
  values: { files: initialFiles, from, subject: initialSubject, text, to },
  withFromField,
  onClose,
  onDeleteAttachment,
  onDeleteRecipient,
  onSend,
}) {
  const { t } = useTranslation()

  const { theme } = useThemeMode()

  const {
    PreviewButtonDelete = EmailActionDeleteButton,
    PreviewButtonSubmit,
    getFilesByRecipient,
    getFilesByRecipientPayload,
    isFullScreen,
    onResize,
  } = useContext(EmailFormContext)

  const [message, setMessage] = useState(EMPTY_STRING)
  const [subject, setSubject] = useState(initialSubject)
  const [currentRecipient, setCurrentRecipient] = useState(1)
  const [recipients, setRecipients] = useState(to)
  const [deleteConfirmation, setDeleteConfirmation] = useState(false)
  const [pagination, setPagination] = useState({
    page: 1,
    pageCount: to.length,
  })
  const [files, setFiles] = useState(initialFiles || EMPTY_LIST)
  const [recipientFiles, setRecipientFiles] = useState(EMPTY_LIST)
  const [generatedPdfIds, setGeneratedPdfIds] = useState(EMPTY_LIST)
  const [isRecipientFilesLoading, setRecipientFilesLoading] = useState(false)

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- updates preview state in response to recipient/pagination changes
    setMessage(text)
    setSubject(initialSubject)

    if (textTemplate?.id) {
      const params = getPreviewTextParams({
        currentRecipient,
        getPreviewDependencies,
        recipients,
        subject: initialSubject,
        templateEntityApiParams,
        text,
      })

      createEmailPreviewTemplateMessageText(textTemplate.id, params).then(
        ({ data }) => {
          setMessage(data.text)
          setSubject(data.subject ?? initialSubject)
        }
      )
    }
  }, [currentRecipient])

  // Sync the current recipient with the pagination page — during render
  // (initial `currentRecipient` and `pagination.page` are both 1).
  const [prevPagination, setPrevPagination] = useState(pagination)

  if (pagination !== prevPagination) {
    setPrevPagination(pagination)
    setCurrentRecipient(pagination.page)
  }

  const selectedRecipient = getSelectedRecipient(
    recipients[currentRecipient - 1]
  )

  useEffect(() => {
    if (selectedRecipient && getFilesByRecipient) {
      const abortController = new AbortController()
      const { signal } = abortController

      // eslint-disable-next-line react-hooks/set-state-in-effect -- updates preview state in response to recipient/pagination changes
      setRecipientFilesLoading(true)

      getFilesByRecipient(getRecipientRealId(selectedRecipient.label))
        .then(({ data }) => {
          if (!signal.aborted) {
            setRecipientFiles(getFilesByRecipientPayload(data))
          }
        })
        .catch(() => {
          if (!signal.aborted) {
            setRecipientFiles(EMPTY_LIST)
          }
        })
        .finally(() => {
          if (!signal.aborted) {
            setRecipientFilesLoading(false)
          }
        })

      return () => {
        abortController.abort()
      }
    }
  }, [selectedRecipient])

  const previewFiles = [...recipientFiles, ...files, ...linkedFiles]

  const attachmentFiles = [...recipientFiles, ...files]

  const { activeFileIndex, handleClickFile, handleCloseFilePreview } =
    useFilePreviewGallery({
      files: previewFiles,
    })

  function handleChangePagination(pagination) {
    setPagination(pagination)
    setCurrentRecipient(pagination.page)
  }

  function handleShowTemplatePdf({ file, pdfId }) {
    setFiles(prevFiles =>
      getNextFiles({ fileId: pdfId, files: prevFiles, templateFile: file })
    )

    handleClickFile(pdfId)
  }

  function handleClickPreviewFile(file) {
    const recipient = recipients[currentRecipient - 1]

    const generatedPdf = getGeneratedTemplatePdf({
      file,
      generatedPdfIds,
      recipient,
    })

    if (generatedPdf) {
      handleShowTemplatePdf({ file, pdfId: generatedPdf.id })
    } else if (file.template) {
      getEmailGoogleTemplatePreview({
        dependencies: getPreviewGoogleTemplateDependencies({
          baseDependencies: file.template.dependencies,
          dependencyValues: getPreviewDependencies?.(recipient),
        }),
        external_variables: file.template.external_variables,
        recipient: getPreviewRecipientValue(recipient),
        template: file.template.id,
      }).then(({ data }) => {
        if (data.file_id) {
          setGeneratedPdfIds(v => [
            ...v,
            {
              id: data.file_id,
              recipient: recipient.value,
              template: file.template,
            },
          ])

          handleShowTemplatePdf({ file, pdfId: data.file_id })
        }
      })
    } else {
      handleClickFile(file.id)
    }
  }

  function handleClickMessageLink(event) {
    const link = event.target?.closest?.(`a[${LINKED_FILES_ITEM_ID_ATTRIBUTE}]`)

    if (link) {
      const fileId = Number(link.getAttribute(LINKED_FILES_ITEM_ID_ATTRIBUTE))
      const file = linkedFiles.find(item => item.id === fileId)

      if (Number.isFinite(fileId) && file) {
        preventDefault(event)
        stopPropagation(event)

        handleClickPreviewFile(file)
      }
    }
  }

  function handleDeleteFile(file) {
    if (file.template) {
      setFiles(files.filter(f => f.template !== file.template))
    } else {
      setFiles(files.filter(f => f.id !== file.id))
    }

    onDeleteAttachment(file)
  }

  function renderFile(file, key) {
    return (
      <FileAttachTag
        file={file}
        height={TAG_HEIGHT.height40}
        isSkeleton={isRecipientFilesLoading}
        key={key}
        onClick={handleClickPreviewFile}
        onRemove={
          file.canDelete === undefined || file.canDelete === true
            ? handleDeleteFile
            : undefined
        }
      />
    )
  }

  function handleChangeRecipient(option) {
    setPagination({
      ...pagination,
      page:
        recipients.findIndex(recipient => recipient.value === option.value) + 1,
    })
  }

  function deleteRecipient() {
    setDeleteConfirmation(false)

    onDeleteRecipient(
      recipients.filter(
        recipient => recipients[currentRecipient - 1].value !== recipient.value
      )
    )

    if (recipients.length === 1) {
      onClose()

      return
    }

    setCurrentRecipient(getPrevRecipientIndex(currentRecipient))
    setPagination({
      page: getPrevRecipientIndex(currentRecipient),
      pageCount: recipients.length - 1,
    })
    setRecipients(
      recipients.filter(
        recipient => recipients[currentRecipient - 1].value !== recipient.value
      )
    )
  }

  function handleCancelCloseConfirm() {
    setDeleteConfirmation(false)
  }

  function handleDeleteRecipient() {
    setDeleteConfirmation(true)
  }

  if (deleteConfirmation) {
    return (
      <ConfirmationModal
        cancelText={t('Modal.CommonGoBackBtnKey')}
        confirmationText={t('Modal.DeleteBtnKey')}
        description={t('Modal.DeleteConfirmationTitleKey')}
        onCancel={handleCancelCloseConfirm}
        onSubmit={deleteRecipient}
      />
    )
  }

  const boxClassName = classNames({ [styles.box]: !isFullScreen })

  const messageQaProps = getQaSelector({
    value: QA_NAME_MESSAGE_FIELD,
  })

  return (
    <Modal qaName={QA_NAME_PREVIEW_MODAL} onClose={onClose}>
      <Box className={boxClassName} isFullScreen={isFullScreen}>
        <Header
          isFullScreen={isFullScreen}
          text={t('Email.EmailPreviewTitleKey')}
          onClose={onClose}
          onResize={onResize}
        />
        <Content
          isFullScreen={isFullScreen}
          size={isFullScreen ? SIZE.X_LARGE : SIZE.MEDIUM}
        >
          <div className={styles.switchRecipients}>
            <ButtonSelect
              Control={ButtonTextSelectControl}
              className={styles.control}
              controlProps={{
                buttonProps: {
                  Button: Button.Outlined,
                  className: styles.controlButton,
                  height: BUTTON_HEIGHT_40,
                  text: t('Email.EmeailPreviewSelectRecipientKey'),
                },
              }}
              dropdownFloatingConfig={{
                dropdownPlacement: FLOATING_ELEMENT_PLACEMENT.bottomStart,
                dropdownStyles: {
                  maxHeight: RECIPIENTS_DROPDOWN_MAX_HEIGHT,
                },
                maxWidthByRef: false,
              }}
              dropdownProps={{
                className: styles.dropdown,
              }}
              filter={filterByFullNameAndEmail}
              listProps={{
                Header: SelectDropdownListHeader,
                headerProps: {
                  Search: SelectDropdownListSearchInput,
                },
              }}
              options={recipients}
              onSelectItem={handleChangeRecipient}
            />
            {recipients.length > 1 ? (
              <TablePaginationNavigation
                nextButtonProps={{
                  text: null,
                }}
                prevButtonProps={{
                  text: null,
                }}
                value={pagination}
                onChange={handleChangePagination}
              >
                <TablePaginationPages
                  value={pagination}
                  onChange={setPagination}
                />
              </TablePaginationNavigation>
            ) : null}
          </div>
          <Divider className={styles.divider} />
          {withFromField ? (
            <EmailPreviewTextField
              label={`${t('Email.FromLabelKey')}:`}
              qaName={QA_NAME_FROM_FIELD}
              value={from.email}
            />
          ) : null}
          <EmailPreviewUserField
            label={`${t('Email.ToLabelKey')}:`}
            option={selectedRecipient}
            qaName={QA_NAME_TO_FIELD}
          />
          <EmailPreviewTextField
            label={`${t('Email.SubjectLabelKey')}:`}
            qaName={QA_NAME_SUBJECT_FIELD}
            value={subject}
          />
          <div
            {...messageQaProps}
            className={styles.message}
            dangerouslySetInnerHTML={{
              __html: getThemeContentHtml(
                openHtmlLinksInNewTab(message),
                theme
              ),
            }}
            onClick={handleClickMessageLink}
          />
          {attachmentFiles?.length > 0 || isRecipientFilesLoading ? (
            <FileList
              className={styles.fileList}
              files={attachmentFiles}
              isSkeleton={isRecipientFilesLoading}
              itemClassName={styles.fileListItem}
              renderItem={renderFile}
            />
          ) : null}
          <FilesPreviewGallery
            files={previewFiles}
            initActive={activeFileIndex}
            scope={FILE_SCOPE_EMAIL}
            onClose={handleCloseFilePreview}
          />
        </Content>
        <FormActions
          ButtonAdd={null}
          ButtonClear={null}
          ButtonSubmit={PreviewButtonSubmit}
          UploadComponent={null}
          cancelProps={{
            onClick: onClose,
          }}
          className={styles.actions}
          submitProps={{
            title: 'Emails.SendMassBntKey',
            translationParams: { count: recipients.length },
            onClick: onSend,
          }}
        >
          {PreviewButtonDelete ? (
            <PreviewButtonDelete
              tooltip={t('Emails.DeleteRecipientBtnKey')}
              onClick={handleDeleteRecipient}
            />
          ) : null}
        </FormActions>
      </Box>
    </Modal>
  )
}

export default EmailPreviewModal
